import { HTTPError } from "ky";

import type { ApiError } from "~/shared/types/api-types.ts";

const isRecord = (value: unknown): value is Record<string, unknown> =>
	Boolean(value) && typeof value === "object" && !Array.isArray(value);

/**
 * Unwraps a single layer of JSON string encoding so a double-encoded value
 * like `"\"User name already exists.\""` becomes `User name already exists.`.
 */
const unwrapQuotedString = (value: string): string => {
	const trimmed = value.trim();
	if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
		try {
			const parsed: unknown = JSON.parse(trimmed);
			if (typeof parsed === "string") {
				const inner = parsed.trim();
				if (inner.length > 0) {
					return inner;
				}
			}
		} catch {
			// Not valid JSON; fall through and return the trimmed original.
		}
	}
	return trimmed;
};

/**
 * `onError` handler for the vendor user mutation endpoints (Add/Update/Delete
 * VendorWebUser, both internal and external). On failure these return a
 * singular `error` field, occasionally double-encoded as a JSON string, e.g.
 * `{"error":"\"User name already exists.\""}`. The shared `extractApiError`
 * only understands `message`/`detail`/`errors` shapes, so this vendor-scoped
 * handler surfaces the `error` field for these specific calls. Returns null for
 * any other shape so the fetch client's default extraction still applies.
 */
export const extractVendorUserError = async (
	error: unknown,
): Promise<ApiError | null> => {
	if (!(error instanceof HTTPError)) {
		return null;
	}

	try {
		const text = await error.response.clone().text();
		const body: unknown = JSON.parse(text);
		if (!isRecord(body)) {
			return null;
		}

		const rawError = body["error"] ?? body["Error"];
		if (typeof rawError !== "string") {
			return null;
		}

		const message = unwrapQuotedString(rawError);
		if (message.length === 0) {
			return null;
		}

		return {
			success: false,
			error: message,
			details: body,
			status: error.response.status,
		};
	} catch {
		// Non-JSON body or no usable `error` field — let the default extraction
		// (extractApiError) handle it.
		return null;
	}
};
